import React, { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import AdminLayout from "@/components/layout/AdminLayout";
import CampOpsNav from "@/components/CampOpsNav";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Award, Sparkles, Send, Trash2, Image, ExternalLink, Star, CheckCircle2 } from "lucide-react";

const CATEGORY_LABELS = {
  overall: "Overall Awards",
  offensive: "Offensive Awards",
  defensive: "Defensive Awards",
  athletic: "Athletic Awards",
  pitching: "Pitching Awards",
  position: "Position Awards",
};
const CATEGORY_ORDER = ["overall", "offensive", "defensive", "athletic", "pitching", "position"];

const SOURCE_BADGE = {
  metric: "bg-[#E63946]/10 text-[#E63946]",
  evaluation: "bg-indigo-100 text-indigo-700",
  rating: "bg-[#0A192F]/10 text-[#0A192F]",
  manual: "bg-amber-100 text-amber-700",
};

export default function CampRecognition() {
  const { campId } = useParams();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [createDrafts, setCreateDrafts] = useState(true);
  const [mvpPlayer, setMvpPlayer] = useState("");

  const load = useCallback(() => {
    api.get(`/admin/camps/${campId}/recognition`)
      .then(({ data }) => setData(data))
      .catch(() => setData({ camp: null, awards: [], grouped: {}, roster: [] }));
  }, [campId]);

  useEffect(() => { load(); }, [load]);

  const generate = async () => {
    setBusy(true);
    try {
      const { data: res } = await api.post(`/admin/camps/${campId}/recognition/generate`);
      toast.success(`Generated ${res.stats.created + res.stats.updated} awards${res.stats.skipped_published ? ` · ${res.stats.skipped_published} published kept` : ""}`);
      load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  const publish = async () => {
    setBusy(true);
    try {
      const { data: res } = await api.post(`/admin/camps/${campId}/recognition/publish`, null, { params: { create_drafts: createDrafts } });
      toast.success(`Published ${res.published} awards${createDrafts ? ` · ${res.media_drafts_created} media drafts queued` : ""}`);
      load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  const setMvp = async () => {
    if (!mvpPlayer) return;
    try {
      await api.put(`/admin/camps/${campId}/recognition/mvp`, { player_id: mvpPlayer });
      toast.success("MVP set");
      setMvpPlayer("");
      load();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || e.message); }
  };

  const changeWinner = async (award, playerId) => {
    try {
      await api.patch(`/admin/awards/${award.id}`, { player_id: playerId });
      load();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || e.message); }
  };

  const toggleStatus = async (award) => {
    try {
      await api.patch(`/admin/awards/${award.id}`, { status: award.status === "published" ? "draft" : "published" });
      load();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || e.message); }
  };

  const removeAward = async (award) => {
    try { await api.delete(`/admin/awards/${award.id}`); load(); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || e.message); }
  };

  const mediaDraft = async (award) => {
    try {
      await api.post(`/admin/awards/${award.id}/media-draft`);
      toast.success("Media draft queued");
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || "Player has not consented to media use"); }
  };

  if (!data) return <AdminLayout><div className="p-8 label-overline">Loading recognition engine…</div></AdminLayout>;

  const awards = data.awards || [];
  const draftCount = awards.filter((a) => a.status === "draft").length;
  const publishedCount = awards.filter((a) => a.status === "published").length;
  const roster = data.roster || [];

  return (
    <AdminLayout>
      <div className="p-4 sm:p-8 max-w-6xl">
        <Link to="/admin/camps" className="label-overline text-slate-500 hover:text-[#E63946]">← All Camps</Link>
        <div className="flex items-center gap-2 label-overline text-[#E63946] mt-3"><Award className="w-4 h-4" /> Recognition Engine</div>
        <h1 data-testid="recognition-title" className="font-display text-3xl sm:text-4xl uppercase tracking-tighter mt-2">{data.camp?.title || "Camp"}</h1>
        <p className="text-xs text-slate-500 mt-1 max-w-2xl">Auto-computes award winners from verified metrics, submitted evaluations, and CBR ratings. Review, set MVP, then publish — nothing goes public or to social until you publish/approve.</p>

        <CampOpsNav campId={campId} />

        {/* Action bar */}
        <div className="bg-white border border-slate-200 rounded-sm p-4 flex flex-col sm:flex-row sm:items-center gap-3 flex-wrap">
          <button data-testid="recognition-generate-btn" onClick={generate} disabled={busy}
            className="inline-flex items-center justify-center gap-2 bg-[#0A192F] hover:bg-[#1a2a4a] text-white px-5 py-2.5 text-sm font-semibold uppercase tracking-wider rounded-sm disabled:opacity-50">
            <Sparkles className="w-4 h-4" /> Generate Recognition
          </button>
          <div className="flex items-center gap-4 text-xs text-slate-500">
            <span><span className="font-data text-lg text-amber-600">{draftCount}</span> draft</span>
            <span><span className="font-data text-lg text-emerald-600">{publishedCount}</span> published</span>
          </div>
          <div className="flex-1" />
          <label className="flex items-center gap-2 text-xs text-slate-600 select-none">
            <input data-testid="recognition-create-drafts-toggle" type="checkbox" checked={createDrafts} onChange={(e) => setCreateDrafts(e.target.checked)} className="w-4 h-4" />
            Also queue Media Engine drafts
          </label>
          <button data-testid="recognition-publish-btn" onClick={publish} disabled={busy || draftCount === 0}
            className="inline-flex items-center justify-center gap-2 bg-[#E63946] hover:bg-[#D90429] text-white px-5 py-2.5 text-sm font-semibold uppercase tracking-wider rounded-sm disabled:opacity-40">
            <Send className="w-4 h-4" /> Publish {draftCount || ""}
          </button>
          {publishedCount > 0 && data.camp?.slug && (
            <Link to={`/camps/${data.camp.slug}/awards`} target="_blank" className="inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-wider text-[#0A192F] hover:text-[#E63946]">
              View public page <ExternalLink className="w-3.5 h-3.5" />
            </Link>
          )}
        </div>

        {/* MVP picker */}
        <div className="bg-amber-50 border border-amber-200 rounded-sm p-4 mt-4 flex flex-col sm:flex-row sm:items-center gap-3">
          <div className="flex items-center gap-2 label-overline text-amber-700"><Star className="w-4 h-4" /> MVP (admin selects)</div>
          <select data-testid="mvp-select" value={mvpPlayer} onChange={(e) => setMvpPlayer(e.target.value)} className="flex-1 sm:max-w-sm px-3 py-2 border border-amber-300 rounded-sm text-sm bg-white">
            <option value="">Select MVP…</option>
            {roster.map((p) => <option key={p.id} value={p.id}>{p.name} · {p.position || "—"} · {p.grad_year || "—"}{p.cbr_overall_rating != null ? ` · CBR ${Number(p.cbr_overall_rating).toFixed(1)}` : ""}</option>)}
          </select>
          <button data-testid="mvp-set-btn" onClick={setMvp} disabled={!mvpPlayer} className="bg-amber-600 hover:bg-amber-700 text-white px-4 py-2 text-xs font-semibold uppercase tracking-wider rounded-sm disabled:opacity-40">Set MVP</button>
        </div>

        {awards.length === 0 ? (
          <div className="mt-8 bg-white border border-dashed border-slate-300 rounded-sm p-10 text-center text-sm text-slate-500">
            No awards yet. Click <strong>Generate Recognition</strong> to auto-compute award winners from this camp's data.
          </div>
        ) : (
          <div className="mt-6 space-y-8">
            {CATEGORY_ORDER.filter((c) => (data.grouped || {})[c]?.length).map((cat) => (
              <section key={cat} data-testid={`recognition-category-${cat}`}>
                <h2 className="label-overline text-[#0A192F] border-b border-slate-200 pb-2">{CATEGORY_LABELS[cat]}</h2>
                <div className="grid sm:grid-cols-2 gap-3 mt-3">
                  {data.grouped[cat].map((a) => (
                    <div key={a.id} data-testid={`award-card-${a.award_key}`} className="bg-white border border-slate-200 rounded-sm p-4">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <div className="flex items-center gap-1.5 label-overline text-[#E63946]"><Award className="w-3.5 h-3.5" /> {a.award_label}</div>
                          <div className="font-semibold mt-1 truncate">{a.player_name}</div>
                          <div className="text-[11px] text-slate-500">{[a.player_position, a.player_grad_year].filter(Boolean).join(" · ")}</div>
                        </div>
                        <div className="text-right shrink-0">
                          <div className="font-data text-2xl text-[#0A192F] leading-none">{a.stat_value}{a.stat_units ? <span className="text-sm text-slate-400"> {a.stat_units}</span> : ""}</div>
                          <div className="text-[9px] uppercase tracking-wider text-slate-400 mt-0.5">{a.stat_label}</div>
                        </div>
                      </div>
                      <div className="flex items-center gap-1.5 mt-2 flex-wrap">
                        <span className={`text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm ${SOURCE_BADGE[a.source] || "bg-slate-100 text-slate-600"}`}>{a.source}</span>
                        <span data-testid={`award-status-${a.award_key}`} className={`text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm ${a.status === "published" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>{a.status}</span>
                      </div>
                      <div className="mt-3 flex items-center gap-2 flex-wrap">
                        <select data-testid={`award-winner-select-${a.award_key}`} value={a.player_id} onChange={(e) => changeWinner(a, e.target.value)} className="flex-1 min-w-0 px-2 py-1.5 border border-slate-300 rounded-sm text-xs bg-white">
                          {!roster.some((p) => p.id === a.player_id) && <option value={a.player_id}>{a.player_name}</option>}
                          {roster.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                        </select>
                        <button data-testid={`award-toggle-${a.award_key}`} onClick={() => toggleStatus(a)} title={a.status === "published" ? "Unpublish" : "Publish"} className="p-1.5 border border-slate-300 rounded-sm hover:border-emerald-500 text-emerald-600"><CheckCircle2 className="w-4 h-4" /></button>
                        <button data-testid={`award-media-draft-${a.award_key}`} onClick={() => mediaDraft(a)} title="Queue media draft" className="p-1.5 border border-slate-300 rounded-sm hover:border-[#E63946] text-[#E63946]"><Image className="w-4 h-4" /></button>
                        <button data-testid={`award-delete-${a.award_key}`} onClick={() => removeAward(a)} title="Remove" className="p-1.5 border border-slate-300 rounded-sm hover:border-slate-500 text-slate-500"><Trash2 className="w-4 h-4" /></button>
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            ))}
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
